import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  ContainerPricingModel,
  Product,
} from '../../../../modules/productsListing/types'
import { FormikProps, useFormikContext } from 'formik'
import {
  BoxV4,
  DatePickerV2,
  labraUIDateFormat,
  PlanType,
  RadioButtonType,
  RadioButtonsV2,
  SelectV2,
  TextInputV4,
} from '@labrav/react-components'
import { SelectChangeEvent, Typography } from '@mui/material'
import { useStyles } from './PrivateOfferContractDuration.styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faListCheck } from '@fortawesome/pro-regular-svg-icons'
import { get, isEmpty } from 'lodash'
import { getContractDurationOptions } from '../../../../utils/contractDurationOptions'
import { OffersWithErrors } from '../../../../modules/privateOffer/privateOfferCreation/types'
import {
  canAddDuration,
  canAddDurationForContract,
  formatContractDurationBetweenDates,
  getContractDurationMode,
  isOneHourContractDuration,
} from '../../../../utils/containerOfferHelperFunctions'
import { DataType } from '@labrav/react-components/lib/@types/types/forms'
import {
  FieldMetadata,
  FieldMetadataField,
} from '../../../../modules/privateOffer/types'
import { useSelector } from '../../../../store'
import { DetailInfo } from '../../../../common/components/DetailRow/DetailRow'
import { DateTime } from 'luxon'
import {
  awsMaxContractDurationMonths,
  awsMaxContractDurationYears,
} from '../../../../common/utils/constants'

const emptyDim = [
  {
    label: '',
    labelDescription: '',
    type: 'standard',
    isAdditionalUsageAllowed: false,
    currency: 'USD',
  },
]

const startDateOptions = [
  {
    label: 'Accepted date',
    value: true,
  },
  {
    label: 'Specified date',
    value: false,
  },
]

const contractDurationModeOptions = [
  {
    key: 'duration',
    label: 'Enter a contract duration',
    type: RadioButtonType.STANDARD,
  },
  {
    key: 'endDate',
    label: 'Select an end date',
    type: RadioButtonType.STANDARD,
  },
]

export type PrivateOfferContractDurationProps = {
  product: Product
}
export const PrivateOfferContractDuration: React.FC<
  PrivateOfferContractDurationProps
> = ({ product }) => {
  const {
    values,
    errors,
    touched,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext() as FormikProps<OffersWithErrors>
  const { classes } = useStyles()

  const privateOfferAgreementStartDateField = {
    title: 'Select a start date',
    humanReadableKey: 'Select a start date',
    placeHolder: 'Select a start date',
    id: 'agreementStartDate',
    isRequired: false,
    isReadOnly: false,
    isCustom: false,
    dataType: 'date',
    minDate: DateTime.now().plus({ days: 1 }).toISODate(),
  }
  const formikProps = useFormikContext() as FormikProps<OffersWithErrors>
  const [isCustomDuration, setIsCustomDuration] = useState(
    canAddDurationForContract(values?.serviceLength)
  )
  const [durationMode, setDurationMode] = useState<'duration' | 'endDate'>(() =>
    getContractDurationMode(values)
  )
  const isModePickedByUser = useRef(false)
  const contractDurationMode = get(values, 'contractDurationMode') as
    | 'duration'
    | 'endDate'
    | undefined

  useEffect(() => {
    if (contractDurationMode === durationMode) return
    void setFieldValue('contractDurationMode', durationMode, false)
  }, [durationMode, setFieldValue, contractDurationMode])

  useEffect(() => {
    if (durationMode !== 'endDate' || values.serviceLength == null) return
    void setFieldValue('serviceLength', null, false)
  }, [durationMode, values.serviceLength, setFieldValue])

  useEffect(() => {
    if (isModePickedByUser.current) return
    if (
      !isEmpty(values.subscriptionEndDate) &&
      values.serviceLength == null &&
      durationMode !== 'endDate'
    ) {
      setDurationMode('endDate')
    }
  }, [durationMode, values.serviceLength, values.subscriptionEndDate])

  const fieldMetadata = useSelector<FieldMetadata | undefined>(
    state =>
      state.privateOffersInCreation?.currentPrivateOfferInCreation?.[
        product.productId
      ]?.fieldMetadata
  )

  const isContractDurationReadOnly = (
    fieldMetadata?.serviceLength as FieldMetadataField
  )?.readOnly
  const isContractDurationHidden = (
    fieldMetadata?.serviceLength as FieldMetadataField
  )?.hidden

  const isAgreementStartDateReadOnly = (
    fieldMetadata?.agreementStartDate as FieldMetadataField
  )?.readOnly
  const isAgreementStartDateHidden = (
    fieldMetadata?.agreementStartDate as FieldMetadataField
  )?.hidden

  const isContainerPricingModalNotContract =
    product?.containerPricingModel !== ContainerPricingModel.CONTRACT

  const contractDurationOptions = getContractDurationOptions(
    isContainerPricingModalNotContract &&
      !(product?.productType === PlanType.SAAS_CONTRACT),
    values.flexiblePaymentTerms as boolean
  )

  const privateOfferEndDateField = {
    title: 'Select end date',
    humanReadableKey: 'Select end date',
    placeHolder: 'Select end date',
    id: 'subscriptionEndDate',
    isRequired: durationMode === 'endDate',
    isReadOnly: false,
    isCustom: false,
    dataType: 'date',
    minDate:
      values.variableStartDate === false && values.agreementStartDate
        ? DateTime.fromISO(values.agreementStartDate as string)
            .plus({ days: 1 })
            .toISODate()
        : DateTime.now().plus({ days: 1 }).toISODate(),
    maxDate: DateTime.now()
      .plus({ years: awsMaxContractDurationYears })
      .toISODate(),
  }
  const standardProductDimensions = useMemo(() => {
    return (
      product?.productDimensions?.filter(
        dimension => dimension.isAdditionalUsageAllowed === false
      ) || []
    )
  }, [product.productDimensions])

  const checkedValue = () => {
    const selectedKey = values.containerOfferServiceLength
    const isValidKey = contractDurationOptions.some(
      option => option.key === selectedKey || selectedKey === ''
    )
    return isValidKey ? selectedKey : 'custom'
  }
  const handleSelection = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value as string
    const additionalDimensions = (values?.dimensions || []).filter(
      dimension => dimension.isAdditionalUsageAllowed === true
    )
    if (isOneHourContractDuration(values.containerOfferServiceLength)) {
      if (
        product.containerPricingModel === ContainerPricingModel.HOURLY ||
        product.containerPricingModel === ContainerPricingModel.USAGE
      ) {
        if (
          values.flexiblePaymentTerms &&
          product.containerPricingModel === ContainerPricingModel.USAGE
        ) {
          const updatedDimensions = [...emptyDim, ...additionalDimensions]
          await setFieldValue('dimensions', updatedDimensions)
        } else {
          const dimensionsWithType = standardProductDimensions.map(
            dimension => ({
              ...dimension,
              type: 'standard',
              dimensionId: dimension?.id,
            })
          )
          const updatedDimensions = [
            ...dimensionsWithType,
            ...additionalDimensions,
          ]
          await setFieldValue('dimensions', updatedDimensions)
        }
        await setFieldTouched('dimensions', false)
      }
    }

    if (value === 'custom') {
      await setFieldTouched('containerOfferServiceLength', false)
      await setFieldValue('containerOfferServiceLength', null)
    } else {
      await setFieldValue('containerOfferServiceLength', value)

      if (isOneHourContractDuration(value)) {
        const updatedDimensions = [...additionalDimensions]
        await setFieldValue('dimensions', updatedDimensions)
      }
    }
  }
  const checkedValueForSaasContract = () => {
    const selectedKey = isCustomDuration
      ? null
      : values.serviceLength?.toString()
    const isValidKey = contractDurationOptions.some(
      option =>
        option.key === selectedKey?.toString() || selectedKey?.toString() === ''
    )
    return isValidKey ? selectedKey : 'custom'
  }

  const handleSelectionForSaasContract = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value as string

    if (value === 'custom') {
      await setFieldTouched('serviceLength', false)
      await setFieldValue('serviceLength', null)
      await setFieldValue('subscriptionEndDate', null)
      await setFieldValue('agreementEndDate', null)
      setIsCustomDuration(true)
    } else {
      const months = Number(value)
      await setFieldValue('serviceLength', months)
      setIsCustomDuration(false)
      if (values.variableStartDate === false && values.agreementStartDate) {
        const end = DateTime.fromISO(values.agreementStartDate as string)
          .plus({ months })
          .toISODate()
        await setFieldValue('subscriptionEndDate', end)
        await setFieldValue('agreementEndDate', end)
      } else {
        await setFieldValue('subscriptionEndDate', null)
        await setFieldValue('agreementEndDate', null)
      }
    }
  }

  const handleDurationModeChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const mode = event.target.value as 'duration' | 'endDate'
    isModePickedByUser.current = true
    setDurationMode(mode)
    await setFieldValue('contractDurationMode', mode, false)
    if (mode === 'endDate') {
      await setFieldValue('serviceLength', null)
      setIsCustomDuration(false)
      await setFieldTouched('subscriptionEndDate', true, true)
    } else {
      setIsCustomDuration(
        canAddDurationForContract(values?.serviceLength as number | null)
      )
      if (
        values.serviceLength &&
        values.variableStartDate === false &&
        values.agreementStartDate
      ) {
        const end = DateTime.fromISO(values.agreementStartDate as string)
          .plus({ months: Number(values.serviceLength) })
          .toISODate()
        await setFieldValue('subscriptionEndDate', end)
        await setFieldValue('agreementEndDate', end)
      } else {
        await setFieldValue('subscriptionEndDate', null)
        await setFieldValue('agreementEndDate', null)
      }
    }
  }

  const handleChangeDayMonth = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as string

    if (value) {
      setFieldValue('containerOfferServiceLength', value)
    } else {
      setFieldValue('containerOfferServiceLength', null)
    }
  }
  const handleChangeMonth = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as string

    const parsedServiceLength = Number(value)
    if (!isNaN(parsedServiceLength) && parsedServiceLength > 0) {
      setFieldValue('serviceLength', parsedServiceLength)
      if (values.variableStartDate === false && values.agreementStartDate) {
        const end = DateTime.fromISO(values.agreementStartDate as string)
          .plus({ months: parsedServiceLength })
          .toISODate()
        setFieldValue('subscriptionEndDate', end)
        setFieldValue('agreementEndDate', end)
      } else {
        setFieldValue('subscriptionEndDate', null)
        setFieldValue('agreementEndDate', null)
      }
    } else {
      setFieldValue('serviceLength', null)
      setFieldValue('subscriptionEndDate', null)
      setFieldValue('agreementEndDate', null)
    }
  }

  const isSpecifiedStartDate = values.variableStartDate === false
  const estimatedContractDuration = formatContractDurationBetweenDates(
    values.agreementStartDate as string,
    values.subscriptionEndDate as string
  )
  const showEstimatedContractDuration =
    durationMode === 'endDate' &&
    isSpecifiedStartDate &&
    estimatedContractDuration !== '-'

  const handleVariableStartDateChange = async (event: SelectChangeEvent) => {
    const selected = event.target.value
    const isAccepted = String(selected) === 'true'
    await setFieldValue('variableStartDate', isAccepted)
    if (isAccepted) {
      await setFieldValue('agreementStartDate', null)
      await setFieldValue('agreementEndDate', null)
      await setFieldValue('subscriptionEndDate', null)
      if (durationMode === 'endDate') {
        isModePickedByUser.current = true
        setDurationMode('duration')
        await setFieldValue('contractDurationMode', 'duration', false)
        setIsCustomDuration(
          canAddDurationForContract(values?.serviceLength as number | null)
        )
      }
    }
  }

  return (
    <div data-testid="Contract-duration-section">
      <BoxV4
        heading={'Contract duration'}
        headingIcon={
          <FontAwesomeIcon
            icon={faListCheck}
            style={{ width: '18px', height: '14px' }}
          />
        }
      >
        {' '}
        <div className={classes.sectionContainer}>
          {product?.productType === PlanType.SAAS_CONTRACT ? (
            <>
              {!isAgreementStartDateHidden && (
                <Typography className={classes.sectionHeading}>
                  Start Date
                </Typography>
              )}
              {!isAgreementStartDateHidden && (
                <div className={classes.companyDetailsContainer}>
                  <div>
                    <SelectV2
                      field={{
                        title: 'Contract start date',
                        placeHolder: 'Contract start date',
                        humanReadableKey: 'Contract start date',
                        id: 'variableStartDate',
                        isRequired: false,
                        isReadOnly: isAgreementStartDateReadOnly ?? false,
                        isCustom: false,
                        defaultValue: 'Accepted date',
                        dataType: 'enum' as DataType,
                        options: startDateOptions,
                      }}
                      value={values.variableStartDate}
                      onChange={handleVariableStartDateChange}
                      onBlur={handleBlur}
                      isError={
                        !!(
                          get(touched, 'variableStartDate', false) &&
                          get(errors, 'variableStartDate', false)
                        )
                      }
                      errorText={
                        (!!get(touched, 'variableStartDate', false) &&
                          get(errors, 'variableStartDate', '')) ||
                        ''
                      }
                    />
                  </div>
                  <div className={classes.inputWidth}>
                    {values.variableStartDate === false &&
                    !isAgreementStartDateHidden ? (
                      <div className={classes.inputWidth}>
                        <DatePickerV2
                          format={labraUIDateFormat}
                          field={{
                            ...privateOfferAgreementStartDateField,
                            isReadOnly: isAgreementStartDateReadOnly ?? false,
                          }}
                          views={['year', 'month', 'day']}
                          key={'agreementStartDate'}
                          showErrorHelper={
                            !!touched.agreementStartDate &&
                            !isEmpty(errors.agreementStartDate)
                          }
                          fieldId={'agreementStartDate'}
                          formik={
                            formikProps as unknown as FormikProps<
                              Record<string, unknown>
                            >
                          }
                          errorHelperText={
                            (!!touched.agreementStartDate &&
                              errors.agreementStartDate) ||
                            ''
                          }
                          validate
                        />
                        {!(
                          !!touched.agreementStartDate &&
                          !isEmpty(errors.agreementStartDate)
                        ) && (
                          <Typography className={classes.dateHelperText}>
                            Starts at 00:00 (UTC)
                          </Typography>
                        )}
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              )}
              {!isContractDurationHidden && (
                <>
                  <Typography className={classes.sectionHeading}>
                    Contract duration options
                  </Typography>
                  <div className={classes.radioButtonRow}>
                    <RadioButtonsV2
                      checkedValue={{
                        key: durationMode,
                        value: durationMode,
                      }}
                      name="contractDurationMode"
                      radioButtonMappings={contractDurationModeOptions.map(
                        option => ({
                          ...option,
                          disabled:
                            isContractDurationReadOnly ||
                            (option.key === 'endDate' && !isSpecifiedStartDate),
                          infoDescription:
                            option.key === 'endDate' && !isSpecifiedStartDate
                              ? 'This option is not available for Accepted date.'
                              : undefined,
                        })
                      )}
                      handleChange={handleDurationModeChange}
                    />
                  </div>
                  {durationMode === 'endDate' && isSpecifiedStartDate && (
                    <div className={classes.companyDetailsContainer}>
                      <div className={classes.endDateColumn}>
                        <div>
                          <DatePickerV2
                            format={labraUIDateFormat}
                            field={privateOfferEndDateField}
                            views={['year', 'month', 'day']}
                            key={'subscriptionEndDate'}
                            showErrorHelper={
                              !!touched.subscriptionEndDate &&
                              !isEmpty(errors.subscriptionEndDate)
                            }
                            fieldId={'subscriptionEndDate'}
                            formik={
                              formikProps as unknown as FormikProps<
                                Record<string, unknown>
                              >
                            }
                            errorHelperText={
                              (!!touched.subscriptionEndDate &&
                                errors.subscriptionEndDate) ||
                              ''
                            }
                            validate
                          />
                          {!(
                            !!touched.subscriptionEndDate &&
                            !isEmpty(errors.subscriptionEndDate)
                          ) && (
                            <Typography className={classes.dateHelperText}>
                              Ends at 23:59 (UTC)
                            </Typography>
                          )}
                        </div>
                        {showEstimatedContractDuration && (
                          <DetailInfo
                            title="Estimated contract duration"
                            value={estimatedContractDuration}
                            keyName="estimatedContractDuration"
                            gridSize={12}
                            className={classes.estimatedDuration}
                          />
                        )}
                      </div>
                    </div>
                  )}
                  {durationMode === 'duration' && (
                    <div className={classes.radioButton}>
                      <RadioButtonsV2
                        checkedValue={{
                          key: checkedValueForSaasContract() as string,
                          value: checkedValueForSaasContract() as string,
                        }}
                        name="serviceLength"
                        radioButtonMappings={contractDurationOptions.map(
                          option => ({
                            ...option,
                            disabled: isContractDurationReadOnly,
                          })
                        )}
                        handleChange={handleSelectionForSaasContract}
                      />
                      {checkedValueForSaasContract() === 'custom' && (
                        <div className={classes.textField}>
                          <TextInputV4
                            keyName="serviceLength"
                            title={'Add duration in months'}
                            type="number"
                            placeHolder=""
                            value={(values.serviceLength || '') as string}
                            maxNumber={awsMaxContractDurationMonths}
                            isError={
                              !!(
                                get(touched, 'serviceLength', false) &&
                                get(errors, 'serviceLength', false)
                              )
                            }
                            onChange={handleChangeMonth}
                            isReadOnly={isContractDurationReadOnly ?? false}
                            isRequired={false}
                            errorText={
                              (!!get(touched, 'serviceLength', false) &&
                                get(errors, 'serviceLength', ' ')) ||
                              ' '
                            }
                            showSpinner={false}
                            onBlur={handleBlur}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <div className={classes.radioButton}>
              <RadioButtonsV2
                checkedValue={{
                  key: checkedValue() as string,
                  value: checkedValue() as string,
                }}
                name="containerOfferServiceLength"
                radioButtonMappings={contractDurationOptions.map(option => ({
                  ...option,
                  disabled: isContractDurationReadOnly,
                }))}
                handleChange={handleSelection}
              />
              {canAddDuration(values.containerOfferServiceLength) && (
                <div className={classes.textField}>
                  {' '}
                  <TextInputV4
                    keyName="containerOfferServiceLength"
                    title={`Add duration in ${
                      isContainerPricingModalNotContract ? 'days' : 'months'
                    }`}
                    type="number"
                    placeHolder=""
                    value={(values.containerOfferServiceLength || '') as string}
                    maxNumber={awsMaxContractDurationMonths}
                    isError={
                      !!(
                        get(touched, 'containerOfferServiceLength', false) &&
                        get(errors, 'containerOfferServiceLength', false)
                      )
                    }
                    onChange={handleChangeDayMonth}
                    isReadOnly={isContractDurationReadOnly ?? false}
                    isRequired={false}
                    errorText={
                      (!!get(touched, 'containerOfferServiceLength', false) &&
                        get(errors, 'containerOfferServiceLength', ' ')) ||
                      ' '
                    }
                    showSpinner={false}
                    onBlur={handleBlur}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </BoxV4>
    </div>
  )
}
